/**
 * Centralized service for accessing localStorage with typed methods.
 * Prevents key mistyping and provides a single source of truth for storage keys.
 */
class LocalStorageService {
  private static readonly KEYS = {
    ACCESS_TOKEN: 'access',
    REFRESH_TOKEN: 'refresh',
    SELECTED_TENANT_ID: 'selected_tenant_id',
    AUTH_ERROR: 'auth_error',
    TT_UI_LEDGER: 'ttUiLedger',
    WA_ORDER: 'waOrder',
  } as const;

  /**
   * Get the access token (JWT) for authenticated requests
   */
  getAccessToken(): string | null {
    return localStorage.getItem(LocalStorageService.KEYS.ACCESS_TOKEN);
  }

  /**
   * Set the access token (JWT) for authenticated requests
   */
  setAccessToken(token: string): void {
    localStorage.setItem(LocalStorageService.KEYS.ACCESS_TOKEN, token);
  }

  /**
   * Remove the access token
   */
  removeAccessToken(): void {
    localStorage.removeItem(LocalStorageService.KEYS.ACCESS_TOKEN);
  }

  /**
   * Get the refresh token used for obtaining new access tokens
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(LocalStorageService.KEYS.REFRESH_TOKEN);
  }

  /**
   * Set the refresh token
   */
  setRefreshToken(token: string): void {
    localStorage.setItem(LocalStorageService.KEYS.REFRESH_TOKEN, token);
  }

  /**
   * Remove the refresh token
   */
  removeRefreshToken(): void {
    localStorage.removeItem(LocalStorageService.KEYS.REFRESH_TOKEN);
  }

  /**
   * Get the selected tenant ID for the current user session
   */
  getSelectedTenantId(): string | null {
    return localStorage.getItem(LocalStorageService.KEYS.SELECTED_TENANT_ID);
  }

  /**
   * Set the selected tenant ID for the current user session
   */
  setSelectedTenantId(tenantId: string | number): void {
    localStorage.setItem(LocalStorageService.KEYS.SELECTED_TENANT_ID, String(tenantId));
  }

  /**
   * Remove the selected tenant ID
   */
  removeSelectedTenantId(): void {
    localStorage.removeItem(LocalStorageService.KEYS.SELECTED_TENANT_ID);
  }

  /**
   * Get the authentication error message
   */
  getAuthError(): string | null {
    return localStorage.getItem(LocalStorageService.KEYS.AUTH_ERROR);
  }

  /**
   * Set the authentication error message
   */
  setAuthError(error: string): void {
    localStorage.setItem(LocalStorageService.KEYS.AUTH_ERROR, error);
  }

  /**
   * Remove the authentication error message
   */
  removeAuthError(): void {
    localStorage.removeItem(LocalStorageService.KEYS.AUTH_ERROR);
  }

  /**
   * Get the time tracking UI ledger data
   */
  getTtUiLedger(): Record<string, unknown> {
    try {
      const raw = localStorage.getItem(LocalStorageService.KEYS.TT_UI_LEDGER) || '{}';
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  /**
   * Set the time tracking UI ledger data
   */
  setTtUiLedger(data: Record<string, unknown>): void {
    try {
      localStorage.setItem(LocalStorageService.KEYS.TT_UI_LEDGER, JSON.stringify(data));
    } catch {
      // Silently fail if localStorage is full or unavailable
    }
  }

  /**
   * Get the work area order configuration
   */
  getWaOrder(): number[] {
    try {
      const raw = localStorage.getItem(LocalStorageService.KEYS.WA_ORDER) || '[]';
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }

  /**
   * Set the work area order configuration
   */
  setWaOrder(order: number[]): void {
    try {
      localStorage.setItem(LocalStorageService.KEYS.WA_ORDER, JSON.stringify(order));
    } catch {
      // Silently fail if localStorage is full or unavailable
    }
  }

  /**
   * Clear all authentication-related data from localStorage
   * (tokens, tenant ID, auth errors)
   */
  clearAuthData(): void {
    this.removeAccessToken();
    this.removeRefreshToken();
    this.removeSelectedTenantId();
    this.removeAuthError();
  }

  /**
   * Clear all data from localStorage
   */
  clearAll(): void {
    localStorage.clear();
  }
}

export default new LocalStorageService();
